/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { registry } from './registry';

const REGION_SELECTOR = '[data-page-designer-region-component-types]';

/** Load and register concrete component exports in first-occurrence order. */
export async function registerComponentTypes(typeIds: Iterable<string>): Promise<void> {
    const uniqueTypeIds = [...new Set(typeIds)];
    const results = await Promise.allSettled(uniqueTypeIds.map((id) => registry.loadAndRegister(id)));
    const failures = results.flatMap((result, index) =>
        result.status === 'rejected' ? [{ typeId: uniqueTypeIds[index], cause: result.reason }] : []
    );

    if (failures.length === 1) throw failures[0].cause;
    if (failures.length > 1) {
        throw new AggregateError(
            failures.map(({ cause }) => cause),
            `Failed to register Page Designer component types: ${failures.map(({ typeId }) => typeId).join(', ')}`
        );
    }
}

function parseRegionTypeIds(marker: HTMLElement): string[] {
    const serialized = marker.dataset.pageDesignerRegionComponentTypes;
    if (!serialized) return [];

    try {
        const typeIds: unknown = JSON.parse(serialized);
        return Array.isArray(typeIds) ? typeIds.filter((typeId): typeId is string => typeof typeId === 'string') : [];
    } catch {
        return [];
    }
}

/**
 * Read every region marker currently present in the streamed DOM. Critical regions are
 * prioritized as a group; both groups retain region DOM order and recursive discovery order.
 */
export function getServerRenderedRegionComponentTypeIds(root: ParentNode): string[] {
    const criticalTypeIds = new Set<string>();
    const nonCriticalTypeIds = new Set<string>();

    for (const marker of root.querySelectorAll<HTMLElement>(REGION_SELECTOR)) {
        const target = marker.dataset.pageDesignerRegionCritical === 'true' ? criticalTypeIds : nonCriticalTypeIds;
        for (const typeId of parseRegionTypeIds(marker)) target.add(typeId);
    }

    return [...criticalTypeIds, ...[...nonCriticalTypeIds].filter((typeId) => !criticalTypeIds.has(typeId))];
}

/** Register every component type represented by a server-rendered region marker currently present. */
export function registerServerRenderedComponentTypes(root: ParentNode): Promise<void> {
    return registerComponentTypes(getServerRenderedRegionComponentTypeIds(root));
}
